import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faEye, faTimes } from '@fortawesome/free-solid-svg-icons';

const PPD_TERM_LABELS = {
  1: 'Outright',
  2: 'Rebate',
};

const normalizeNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundCurrency = (value) => {
  const parsed = normalizeNumber(value);
  return Math.round((parsed + Number.EPSILON) * 100) / 100;
};

const formatCurrency = (value) => {
  if (value === 0 || value === undefined || value === null) return '-';
  return new Intl.NumberFormat('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatPercent = (value) =>
  `${new Intl.NumberFormat('en-PH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(value)}%`;

const resolvePpdRateFactor = (rawRate) => {
  const rate = normalizeNumber(rawRate);
  if (rate <= 0) return 0;

  return rate >= 0.5 ? rate / 100 : rate;
};

const resolvePpdRatePercent = (rawRate) => {
  const rate = normalizeNumber(rawRate);
  if (rate <= 0) return 0;

  return rate >= 0.5 ? rate : rate * 100;
};

const VatComputationBreakdown = ({
  transactionAmount = 0,
  ppdTag = 0,
  ppdTerm = null,
  ppdRate = 0,
  providerName = '',
  hmcVatTag = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSpecsOpen, setIsSpecsOpen] = useState(false);

  const amount = normalizeNumber(transactionAmount);
  const ppdTermValue = Number(ppdTerm);
  const ppdRateFactor = resolvePpdRateFactor(ppdRate);
  const ppdRatePercent = resolvePpdRatePercent(ppdRate);
  const isPpdEnabled = Number(ppdTag) === 1 && ppdRateFactor > 0;
  const isPpdRebate = isPpdEnabled && ppdTermValue === 2;
  const isPpdOutright = isPpdEnabled && !isPpdRebate;
  const ppdTermLabel = isPpdEnabled
    ? isPpdRebate
      ? PPD_TERM_LABELS[2]
      : PPD_TERM_LABELS[1]
    : 'Not Applicable';
  const hmcVatLabel =
    String(hmcVatTag) === '1'
      ? 'With VAT in Billing Invoice'
      : String(hmcVatTag) === '0'
      ? 'No VAT in Billing Invoice'
      : 'Not selected';

  const computations = useMemo(() => {
    const taxRatePercent = 2;

    const buildScenario = ({ hasVat }) => {
      const transactionAmt = amount;
      const netOfVatBase = hasVat ? amount / 1.12 : amount;
      const netOfVat = roundCurrency(netOfVatBase);
      const vat = roundCurrency(hasVat ? amount - netOfVatBase : 0);
      const withholdingTax = roundCurrency(netOfVatBase * (taxRatePercent / 100));
      const netPayable = roundCurrency(amount - withholdingTax);
      const ppdAmount = roundCurrency(isPpdEnabled ? netOfVatBase * ppdRateFactor : 0);
      const ppdDeduction = isPpdOutright ? ppdAmount : 0;

      return {
        transactionAmt,
        netOfVat,
        vat,
        taxRate: taxRatePercent,
        withholdingTax,
        netPayable,
        ppdAmount,
        ppdDeduction,
        netPayableAfterPpd: roundCurrency(netPayable - ppdDeduction),
      };
    };

    return {
      withVat: buildScenario({ hasVat: true }),
      noVat: buildScenario({ hasVat: false }),
    };
  }, [amount, isPpdEnabled, isPpdOutright, ppdRateFactor]);

  const ComputationTable = ({ title, data }) => (
    <div className="mb-6">
      <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">{title}</h4>
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-slate-700 text-white">
              <th className="border border-slate-300 px-3 py-2 text-left font-semibold">
                Label
              </th>
              <th className="border border-slate-300 px-3 py-2 text-right font-semibold w-[120px]">
                Amount
              </th>
              <th className="border border-slate-300 px-3 py-2 text-left font-semibold min-w-[220px]">
                Computation
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-slate-50 hover:bg-slate-100">
              <td className="border border-slate-200 px-3 py-2 text-slate-700">
                Transaction Amt.
              </td>
              <td className="border border-slate-200 px-3 py-2 text-right text-slate-700">
                {formatCurrency(data.transactionAmt)}
              </td>
              <td className="border border-slate-200 px-3 py-2 text-slate-600"></td>
            </tr>

            <tr className="bg-slate-50 hover:bg-slate-100">
              <td className="border border-slate-200 px-3 py-2 text-slate-700">
                Net of VAT
              </td>
              <td className="border border-slate-200 px-3 py-2 text-right text-slate-700">
                {formatCurrency(data.netOfVat)}
              </td>
              <td className="border border-slate-200 px-3 py-2 text-slate-600">
                {data.vat === 0 ? '=same as Transaction Amt.' : '=Transaction Amt./1.12'}
              </td>
            </tr>

            <tr className="bg-slate-50 hover:bg-slate-100">
              <td className="border border-slate-200 px-3 py-2 text-slate-700">
                VAT
              </td>
              <td className="border border-slate-200 px-3 py-2 text-right text-slate-700">
                {formatCurrency(data.vat)}
              </td>
              <td className="border border-slate-200 px-3 py-2 text-slate-600">
                =Transaction Amt. - Net of VAT
              </td>
            </tr>

            <tr className="bg-slate-50 hover:bg-slate-100">
              <td className="border border-slate-200 px-3 py-2 text-slate-700">
                Tax Rate
              </td>
              <td className="border border-slate-200 px-3 py-2 text-right text-slate-700">
                {data.taxRate}%
              </td>
              <td className="border border-slate-200 px-3 py-2 text-slate-600">
                *if paid to provider {data.taxRate}%
              </td>
            </tr>

            <tr className="bg-slate-50 hover:bg-slate-100">
              <td className="border border-slate-200 px-3 py-2 text-slate-700">
                Withholding Tax
              </td>
              <td className="border border-slate-200 px-3 py-2 text-right text-slate-700">
                {formatCurrency(data.withholdingTax)}
              </td>
              <td className="border border-slate-200 px-3 py-2 text-slate-600">
                =Net of VAT x Tax Rate
              </td>
            </tr>

            <tr className="bg-slate-50 hover:bg-slate-100">
              <td className="border border-slate-200 px-3 py-2 text-slate-700 font-semibold">
                Net Payable
              </td>
              <td className="border border-slate-200 px-3 py-2 text-right text-slate-700 font-semibold">
                {formatCurrency(data.netPayable)}
              </td>
              <td className="border border-slate-200 px-3 py-2 text-slate-600">
                =Transaction Amount - Withholding tax
              </td>
            </tr>

            {isPpdEnabled && (
              <>
                <tr className="bg-cyan-50 hover:bg-cyan-100">
                  <td className="border border-cyan-100 px-3 py-2 text-slate-700">
                    PPD Tag
                  </td>
                  <td className="border border-cyan-100 px-3 py-2 text-right text-slate-700">
                    Enabled
                  </td>
                  <td className="border border-cyan-100 px-3 py-2 text-slate-600">
                    =Provider PPD setting is active
                  </td>
                </tr>

                <tr className="bg-cyan-50 hover:bg-cyan-100">
                  <td className="border border-cyan-100 px-3 py-2 text-slate-700">
                    PPD Term
                  </td>
                  <td className="border border-cyan-100 px-3 py-2 text-right text-slate-700">
                    {ppdTermLabel}
                  </td>
                  <td className="border border-cyan-100 px-3 py-2 text-slate-600">
                    {isPpdOutright
                      ? '=Outright PPD is deducted from Net Payable'
                      : '=Rebate PPD is displayed only'}
                  </td>
                </tr>

                <tr className="bg-cyan-50 hover:bg-cyan-100">
                  <td className="border border-cyan-100 px-3 py-2 text-slate-700">
                    PPD Rate
                  </td>
                  <td className="border border-cyan-100 px-3 py-2 text-right text-slate-700">
                    {formatPercent(ppdRatePercent)}
                  </td>
                  <td className="border border-cyan-100 px-3 py-2 text-slate-600">
                    =Provider PPD rate
                  </td>
                </tr>

                <tr className="bg-cyan-50 hover:bg-cyan-100">
                  <td className="border border-cyan-100 px-3 py-2 text-slate-700">
                    PPD
                  </td>
                  <td className="border border-cyan-100 px-3 py-2 text-right text-slate-700">
                    {formatCurrency(data.ppdAmount)}
                  </td>
                  <td className="border border-cyan-100 px-3 py-2 text-slate-600">
                    =Net of VAT x PPD Rate
                  </td>
                </tr>

                <tr className="bg-emerald-50 hover:bg-emerald-100">
                  <td className="border border-emerald-100 px-3 py-2 text-slate-800 font-semibold">
                    Net Payable after PPD
                  </td>
                  <td className="border border-emerald-100 px-3 py-2 text-right text-slate-800 font-semibold">
                    {formatCurrency(data.netPayableAfterPpd)}
                  </td>
                  <td className="border border-emerald-100 px-3 py-2 text-slate-600">
                    {isPpdOutright
                      ? '=Net Payable - PPD'
                      : '=Net Payable; rebate PPD is not deducted'}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-800">
            VAT Computation Breakdown
          </h4>
          {isPpdEnabled && (
            <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-600">
              <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2 py-0.5">
                PPD {ppdTermLabel}
              </span>
              <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2 py-0.5">
                Rate {formatPercent(ppdRatePercent)}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setIsSpecsOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors"
            title="View Full Specs"
          >
            <FontAwesomeIcon icon={faEye} />
            <span>View Full Specs</span>
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 bg-white rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors"
              title={isExpanded ? 'Hide Details' : 'Show Details'}
            >
              <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
              <span>{isExpanded ? 'Hide' : 'Show'} Details</span>
            </button>
            <span className="absolute -top-2 -right-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white leading-none shadow-sm">
              New
            </span>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4">
          {amount > 0 ? (
            <div>
              <ComputationTable
                title="Computation if with VAT"
                data={computations.withVat}
              />
              <ComputationTable
                title="Computation if NO VAT"
                data={computations.noVat}
              />
            </div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded px-4 py-3 text-center text-slate-500 text-xs">
              Please enter a transaction amount to view VAT computation breakdown
            </div>
          )}
        </div>
      )}

      {isSpecsOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 px-4 py-6">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-xl bg-white shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">
                  VAT Computation Full Specs
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  {providerName || 'Provider'} | {hmcVatLabel}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsSpecsOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                title="Close"
                aria-label="Close specs"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="max-h-[calc(90vh-73px)] overflow-y-auto px-5 py-4 text-xs text-slate-600">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-3 font-semibold text-slate-700">VAT Rules</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between gap-4">
                      <span>Transaction Amount</span>
                      <span className="font-semibold text-slate-800">{formatCurrency(amount)}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>With VAT Net of VAT</span>
                      <span className="font-semibold text-slate-800">Transaction Amount / 1.12</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>No VAT Net of VAT</span>
                      <span className="font-semibold text-slate-800">Transaction Amount</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>Withholding Tax</span>
                      <span className="font-semibold text-slate-800">Net of VAT x 2%</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>Net Payable</span>
                      <span className="font-semibold text-slate-800">Transaction Amount - WHT</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <h4 className="mb-3 font-semibold text-slate-700">PPD Rules</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between gap-4">
                      <span>PPD Tag</span>
                      <span className="font-semibold text-slate-800">
                        {isPpdEnabled ? 'Enabled' : 'Not Applicable'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>PPD Term</span>
                      <span className="font-semibold text-slate-800">{ppdTermLabel}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>PPD Rate</span>
                      <span className="font-semibold text-slate-800">
                        {isPpdEnabled ? formatPercent(ppdRatePercent) : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>Rate Storage</span>
                      <span className="font-semibold text-slate-800">13.5 or 0.135 = 13.5%</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>PPD Amount</span>
                      <span className="font-semibold text-slate-800">Net of VAT x PPD Rate</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>Outright</span>
                      <span className="font-semibold text-slate-800">Deduct PPD from Net Payable</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>Rebate</span>
                      <span className="font-semibold text-slate-800">Display PPD only</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>Blank Term</span>
                      <span className="font-semibold text-slate-800">Defaults to Outright</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-800">
                PPD is computed from Net of VAT, not Transaction Amount. Withholding Tax and PPD are rounded
                to two decimals before the final Net Payable after PPD amount is shown. The additional Net
                Payable after PPD row applies the provider PPD term rule.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VatComputationBreakdown;
